import { CLIENT_HEADER, PRODUCT_HEADER, isErrorCode, type ApiErrorBody, type ErrorCode, type PlanLimitNoticeDto, type QuotaNoticeDto } from '@dbrain/shared';

/**
 * Phase 15c (D-167): the API client of the web app and the native app. The
 * same requests, error codes and reactions to an ended session; what differs
 * is how a request proves who sends it:
 *  - the browser: its session cookie (the browser adds it), and on writes the
 *    double-submit CSRF token in x-csrf-token (ADR-0003);
 *  - the app: `Authorization: Bearer <token>` and x-oppy-client on every
 *    request, x-oppy-product for the license's product check (D-164…D-166).
 */

/** Error thrown by a request; carries the stable code for localized display (D-39). */
export class ApiClientError extends Error {
  constructor(
    readonly code: ErrorCode,
    readonly status: number,
    message: string,
    readonly requestId?: string,
    readonly fields?: { path: string; code: string }[],
    readonly retryAfter?: number,
    /** SA-023: which token budget is used up, when it resets and its use */
    readonly quota?: QuotaNoticeDto,
    /** Phase 16a: which limit of the person's plan refused (file size, documents, own connections) */
    readonly limit?: PlanLimitNoticeDto,
    /** Phase 16a: a wrong sign-up code: the tries left */
    readonly attemptsLeft?: number,
  ) {
    super(message);
  }
}

export function isApiError(error: unknown, code?: ErrorCode): error is ApiClientError {
  return error instanceof ApiClientError && (code === undefined || error.code === code);
}

export type Method = 'GET' | 'POST' | 'PATCH' | 'DELETE' | 'PUT';

export interface BrowserAuth {
  kind: 'browser';
  /** the double-submit token for writes (a readable cookie, or GET /api/auth/csrf) */
  csrfToken(): Promise<string>;
}

export interface AppAuth {
  kind: 'app';
  /** the session token from the Keychain; null before sign-in */
  token(): string | null;
  /** `ios/0.1.0 (261030.0915)` */
  client: string;
  product(): 'companygpt' | 'companycode';
}

export interface ClientOptions {
  /** '' for the web app (its own origin); the installation's address for the app */
  baseUrl: string;
  auth: BrowserAuth | AppAuth;
  locale(): string;
  /** the platform's fetch (the browser's, or expo/fetch for streaming bodies in the app) */
  fetch?: typeof fetch;
}

export interface ClientEvents {
  /** the session is over (signed out elsewhere, idle, revoked under Oturumlar, refused by the directory) */
  sessionEnded?: () => void;
  /** a write refused because the license is read-only or lacks the product (D-124, D-125) */
  licenseRefused?: () => void;
  /** the installation needs a newer app (D-165) */
  updateRequired?: () => void;
}

export interface ApiClient {
  api<T>(method: Method, path: string, body?: unknown): Promise<T>;
  /** a raw-body upload (files): the same headers and error handling */
  upload<T>(method: 'POST' | 'PUT', path: string, body: Blob | ArrayBuffer | Uint8Array, headers?: Record<string, string>, contentType?: string): Promise<T>;
  /** the headers a request of this method carries, for transports of the platform's own (file uploads, downloads, WebSocket) */
  headers(method: Method, extra?: Record<string, string>): Promise<Record<string, string>>;
  url(path: string): string;
  /** reads an error answer the way api() does */
  failure(status: number, parsed: unknown, statusText?: string): ApiClientError;
  events: ClientEvents;
}

export function createApiClient(options: ClientOptions, events: ClientEvents = {}): ApiClient {
  const doFetch: typeof fetch = options.fetch ?? ((input, init) => fetch(input, init));
  const credentials: RequestCredentials | undefined = options.auth.kind === 'browser' ? 'same-origin' : 'omit';

  const headers = async (method: Method, extra: Record<string, string> = {}): Promise<Record<string, string>> => {
    const out: Record<string, string> = { accept: 'application/json', 'x-ui-locale': options.locale() };
    const auth = options.auth;
    if (auth.kind === 'browser') {
      if (method !== 'GET') out['x-csrf-token'] = await auth.csrfToken();
    } else {
      out[CLIENT_HEADER] = auth.client;
      out[PRODUCT_HEADER] = auth.product();
      const token = auth.token();
      if (token) out.authorization = `Bearer ${token}`;
    }
    return { ...out, ...extra };
  };

  const failure = (status: number, parsed: unknown, statusText = ''): ApiClientError => {
    const error = (parsed as ApiErrorBody | undefined)?.error;
    const code: ErrorCode = isErrorCode(error?.code) ? error.code : status >= 500 ? 'service_unavailable' : 'internal_error';
    if (code === 'unauthenticated') events.sessionEnded?.();
    if (code === 'license_read_only' || code === 'product_not_licensed') events.licenseRefused?.();
    if (code === 'client_update_required') events.updateRequired?.();
    return new ApiClientError(code, status, error?.message ?? statusText, error?.requestId, error?.fields, error?.retryAfter, error?.quota, error?.limit, error?.attemptsLeft);
  };

  const url = (path: string) => `${options.baseUrl}${path}`;

  return {
    events,
    headers,
    url,
    failure,

    async api<T>(method: Method, path: string, body?: unknown): Promise<T> {
      // Phase 16b (D-291): a browser's write the CSRF check refused is tried once more with the token the refusal
      // brought (the session's own; an open page may hold an older one); nothing ran the first time
      for (let attempt = 0; ; attempt += 1) {
        const sent = await headers(method, body !== undefined && method !== 'GET' ? { 'content-type': 'application/json' } : {});
        let response: Response;
        try {
          response = await doFetch(url(path), {
            method,
            headers: sent,
            ...(credentials ? { credentials } : {}),
            ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
          });
        } catch (error) {
          throw new ApiClientError('network_error', 0, (error as Error).message);
        }
        if (response.status === 204) return undefined as T;
        const text = await response.text();
        let parsed: unknown = undefined;
        try {
          parsed = text ? JSON.parse(text) : undefined;
        } catch {
          // non-JSON (e.g. a proxy's error page)
        }
        const refusedCsrf = response.status === 403 && (parsed as ApiErrorBody | undefined)?.error?.code === 'csrf_failed';
        if (refusedCsrf && attempt === 0 && options.auth.kind === 'browser' && method !== 'GET') continue;
        if (!response.ok) throw failure(response.status, parsed, response.statusText);
        return parsed as T;
      }
    },

    async upload<T>(method: 'POST' | 'PUT', path: string, body: Blob | ArrayBuffer | Uint8Array, extra: Record<string, string> = {}, contentType?: string): Promise<T> {
      const type = contentType ?? (typeof Blob !== 'undefined' && body instanceof Blob && body.type ? body.type : 'application/octet-stream');
      let response: Response;
      try {
        response = await doFetch(url(path), {
          method,
          ...(credentials ? { credentials } : {}),
          headers: await headers(method, { 'content-type': type, ...extra }),
          body: body as BodyInit,
        });
      } catch (error) {
        throw new ApiClientError('network_error', 0, (error as Error).message);
      }
      const parsed = (await response.json().catch(() => undefined)) as unknown;
      if (!response.ok) throw failure(response.status, parsed, response.statusText);
      return parsed as T;
    },
  };
}
